import React, { useState, useContext, useEffect } from "react";
import {
  Fab,
  Paper,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Feedback as FeedbackIcon,
  Close,
  Send,
  History,
  BugReport,
  Lightbulb,
} from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import { feedbackAPI } from "../../services/api";

const FeedbackButton = () => {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("submit"); // 'submit' or 'history'
  const [feedbackType, setFeedbackType] = useState("enhancement");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [myFeedback, setMyFeedback] = useState([]);

  useEffect(() => {
    if (open && view === "history") {
      loadMyFeedback();
    }
  }, [open, view]);

  const loadMyFeedback = async () => {
    setLoadingHistory(true);
    try {
      const response = await feedbackAPI.getMyFeedback();
      setMyFeedback(response.data || []);
    } catch (err) {
      console.error("Failed to load feedback history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpen = () => {
    if (!user) {
      return;
    }
    setOpen(true);
    setView("submit");
    setError("");
    setSuccess("");
  };

  const handleClose = () => {
    setOpen(false);
    setView("submit");
    setFeedbackType("enhancement");
    setTitle("");
    setDescription("");
    setError("");
    setSuccess("");
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError("Please fill in both title and description");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await feedbackAPI.submit({
        feedback_type: feedbackType,
        title: title.trim(),
        description: description.trim(),
      });

      setSuccess("Feedback submitted successfully! Thank you for your input.");
      setTitle("");
      setDescription("");

      // Reload history if on history view
      if (view === "history") {
        loadMyFeedback();
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to submit feedback. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "resolved":
        return "success";
      case "acknowledged":
        return "info";
      case "closed":
        return "default";
      default:
        return "warning";
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Fab
        color="primary"
        aria-label="feedback"
        onClick={handleOpen}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1000,
          transition: "transform 0.2s ease-in-out",
          "&:hover": {
            transform: "scale(1.1)",
          },
        }}
      >
        <FeedbackIcon />
      </Fab>

      {/* Chat-style popout dialog */}
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: open ? 100 : -500,
          right: 24,
          width: 400,
          maxHeight: 600,
          zIndex: 999,
          borderRadius: 2,
          overflow: "hidden",
          transition:
            "bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out",
          opacity: open ? 1 : 0,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          bgcolor="primary.main"
          color="primary.contrastText"
        >
          <Typography variant="h6" fontWeight="bold">
            Feedback
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ color: "inherit" }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs
          value={view}
          onChange={(e, newValue) => setView(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<Send />}
            iconPosition="start"
            label="Submit"
            value="submit"
            sx={{ textTransform: "none" }}
          />
          <Tab
            icon={<History />}
            iconPosition="start"
            label="History"
            value="history"
            sx={{ textTransform: "none" }}
          />
        </Tabs>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
          }}
        >
          {view === "submit" ? (
            <>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  onClose={() => setSuccess("")}
                >
                  {success}
                </Alert>
              )}

              <FormControl fullWidth margin="normal">
                <InputLabel>Feedback Type</InputLabel>
                <Select
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                  label="Feedback Type"
                >
                  <MenuItem value="enhancement">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Lightbulb fontSize="small" />
                      <span>Enhancement Request</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="bug">
                    <Box display="flex" alignItems="center" gap={1}>
                      <BugReport fontSize="small" />
                      <span>Bug Report</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Title"
                margin="normal"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="Brief summary of your feedback"
                disabled={loading}
                size="small"
              />

              <TextField
                fullWidth
                label="Description"
                margin="normal"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                placeholder="Please provide detailed information..."
                disabled={loading}
                size="small"
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !title.trim() || !description.trim()}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Send />
                  )
                }
                sx={{ mt: 2 }}
              >
                {loading ? "Submitting..." : "Submit Feedback"}
              </Button>
            </>
          ) : (
            <>
              {loadingHistory ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : myFeedback.length === 0 ? (
                <Alert severity="info">
                  You haven't submitted any feedback yet.
                </Alert>
              ) : (
                <Box>
                  {myFeedback.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          {item.feedback_type === "bug" ? (
                            <BugReport fontSize="small" color="error" />
                          ) : (
                            <Lightbulb fontSize="small" color="primary" />
                          )}
                          <Typography variant="subtitle2" fontWeight="bold">
                            {item.title}
                          </Typography>
                        </Box>
                        <Chip
                          label={item.status}
                          size="small"
                          color={getStatusColor(item.status)}
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {item.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.created_at).toLocaleString()}
                        {item.configuration_name &&
                          ` • ${item.configuration_name}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
    </>
  );
};

export default FeedbackButton;
